import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium"
import { LeadRow, LookupMap, StringOptionMap } from "./leadTableTypes"
import DataGridRowMenu from '../../../DataGrid/DataGridRowMenu';
import { buildLookupDisplayColumn, buildOptionDisplayColumn, formatCurrencyFromCents, formatDateTime, formatLookupValue, renderBooleanChip, renderDateTime, renderStatusChip } from "./leadTableCellHelpers"


type BuildLeadTableColumnsParams = {
  timezone: string
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  statuses: StringOptionMap
  dispositions: StringOptionMap
  permitLandingPages: boolean
  userHasMultipleClients: boolean
  userHasMultipleVendors: boolean
  permitDelete: boolean
  onLeadDetails: (row: LeadRow) => void
  onDeleteLead: (row: LeadRow) => void
  clientCampaignCustomAttributes: string[]
}

function insertColumnsAfterField(
  columns: GridColDef<LeadRow>[],
  field: string,
  columnsToInsert: GridColDef<LeadRow>[]
): GridColDef<LeadRow>[] {
  const insertIndex = columns.findIndex((column) => column.field === field)

  if (insertIndex === -1) {
    return [...columns, ...columnsToInsert]
  }

  return [
    ...columns.slice(0, insertIndex + 1),
    ...columnsToInsert,
    ...columns.slice(insertIndex + 1),
  ]
}

function buildBaseColumns(params: {
  timezone: string
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  statuses: StringOptionMap
  dispositions: StringOptionMap
  permitLandingPages: boolean
  userHasMultipleClients: boolean
  userHasMultipleVendors: boolean
  permitDelete: boolean
  onLeadDetails: (row: LeadRow) => void
  onDeleteLead: (row: LeadRow) => void
}): GridColDef<LeadRow>[] {
  const {
    timezone,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    statuses,
    dispositions,
    permitLandingPages,
    userHasMultipleClients,
    userHasMultipleVendors,
    permitDelete,
    onLeadDetails,
    onDeleteLead,
  } = params

  const renderDateCell = renderDateTime(timezone)

  const columns: GridColDef<LeadRow>[] = [
    {
      field: 'createdAt',
      headerName: 'Date',
      minWidth: 170,
      filterable: false,
      valueFormatter: (value) => formatDateTime(value, timezone),
      renderCell: renderDateCell,
      flex: 1,
    },
    {
      field: 'publicId',
      headerName: 'VEGA ID',
      minWidth: 130,
      flex: 1,
    },
    buildLookupDisplayColumn({
      field: 'clientId',
      headerName: 'Client',
      lookup: clientIdToName,
      minWidth: 160,
      type: 'number',
      filterable: false,
      flex: 1,
    }),
    buildLookupDisplayColumn({
      field: 'degreeProgramId',
      headerName: 'Degree Program',
      lookup: degreeProgramIdToName,
      minWidth: 205,
      type: 'number',
      filterable: false,
      flex: 2.5,
    }),
    buildLookupDisplayColumn({
      field: 'vendorId',
      headerName: 'Vendor',
      lookup: vendorIdToName,
      minWidth: 160,
      type: 'number',
      filterable: false,
      flex: 1,
    }),
    buildLookupDisplayColumn({
      field: 'clientCampaignId',
      headerName: 'Campaign',
      lookup: clientCampaignIdToName,
      minWidth: 160,
      type: 'string',
      filterable: true,
      flex: 2.5,
    }),
    {
      field: 'subId',
      headerName: 'SubID',
      minWidth: 140,
      flex: 2.5,
    },
    {
      field: 'source',
      headerName: 'Source',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'firstName',
      headerName: 'First name',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 220,
      flex: 1.5,
    },
    buildOptionDisplayColumn({
      field: 'status',
      headerName: 'Status',
      lookup: statuses,
      minWidth: 155,
      filterable: false,
      renderCell: (params) => renderStatusChip(formatLookupValue(params.row.status, statuses)),
    }),
    {
      field: 'billable',
      headerName: 'Billable',
      minWidth: 160,
      type: 'boolean',
      filterable: false,
      renderCell: renderBooleanChip,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'costInCents',
      headerName: 'Lead amount',
      minWidth: 130,
      type: 'number',
      valueFormatter: formatCurrencyFromCents,
      flex: 1,
    },
    {
      field: 'filteredReason',
      headerName: 'Filtered reason',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'externalId',
      headerName: 'External ID',
      minWidth: 140,
      flex: 2
    },
    {
      field: 'test',
      headerName: 'Test',
      minWidth: 115,
      type: 'boolean',
      filterable: false,
      sortable: false,
      headerAlign: 'left',
      renderCell: renderBooleanChip,
      flex: 1,
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      minWidth: 160,
      flex: 1,
    },
    {
      field: 'streetAddress',
      headerName: 'Street Address',
      minWidth: 220,
      flex: 2.5
    },
    {
      field: 'city',
      headerName: 'City',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'state',
      headerName: 'State',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'zipCode',
      headerName: 'ZIP Code',
      minWidth: 110,
      flex: 1,
    },
    {
      field: 'statusCode',
      headerName: 'Status Code',
      minWidth: 120,
      flex: 1,
    },
    buildOptionDisplayColumn({
      field: 'disposition',
      headerName: 'Disposition',
      lookup: dispositions,
      minWidth: 180,
      filterable: false,
    }),
    {
      field: 'statusChangedAt',
      headerName: 'Status changed',
      minWidth: 205,
      filterable: false,
      valueFormatter: (value) => formatDateTime(value, timezone),
      renderCell: renderDateCell,
      flex: 1,
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'userAgent',
      headerName: 'User Agent',
      minWidth: 280,
      flex: 3
    },
    {
      field: 'secondaryTcpa',
      headerName: 'Secondary TCPA',
      minWidth: 210,
      type: 'boolean',
      filterable: false,
      renderCell: renderBooleanChip,
      flex: 1,
      headerAlign: 'left',
    },
    {
      field: 'rejectionReason',
      headerName: 'Rejection reason',
      minWidth: 180,
      flex: 3
    },
    {
      field: 'rawRequestParameters',
      headerName: 'Raw request parameters',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'trackingParameters',
      headerName: 'Tracking parameters',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'rowMenu',
      headerName: '',
      renderCell: (params: GridRenderCellParams<LeadRow>) => {
        return (
          <DataGridRowMenu
            row={params.row}
            label={`Lead ${params.row.id} menu`}
            menuWidth={140}
            items={[
              {
                key: 'lead-details',
                label: 'Lead Details',
                onClick: onLeadDetails,
              },
              ...(permitDelete
                ? [
                    {
                      key: 'delete-lead',
                      label: 'Delete Lead',
                      onClick: onDeleteLead,
                    },
                  ]
                : []),
            ]}
          />
        )
      },
      hideable: false,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      width: 52,
      disableColumnMenu: true,
      pinnable: false,
      headerClassName: 'menu-col',
      cellClassName: 'menu-col',
    },
  ]

  if (permitLandingPages) {
    columns.push({
      field: 'landingPageId',
      headerName: 'Landing Page ID',
      minWidth: 150,
    })
  }

  return columns
    .filter((column) => userHasMultipleClients || column.field !== 'clientId')
    .filter((column) => userHasMultipleVendors || column.field !== 'vendorId')
}

function buildCustomAttributeColumns(
  clientCampaignCustomAttributes: string[]
): GridColDef<LeadRow>[] {
  return clientCampaignCustomAttributes.map((attribute) => ({
    field: `clientCampaignCustomAttributes:${attribute}`,
    headerName: attribute,
    minWidth: 180,
    sortable: true,
    filterable: true,
    valueGetter: (_value, row) => row.clientCampaignCustomAttributes?.[attribute] ?? '',
    renderCell: (params) => {
      const value = params.value == null ? '' : String(params.value)
      return <span title={value}>{value}</span>
    },
  }))
}

export function buildLeadTableColumns(
  params: BuildLeadTableColumnsParams
): GridColDef<LeadRow>[] {
  const baseColumns = buildBaseColumns(params)
  const customAttributeColumns = buildCustomAttributeColumns(
    params.clientCampaignCustomAttributes
  )

  return insertColumnsAfterField(baseColumns, 'billable', customAttributeColumns)
}
